import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WorkshopSiteVisitModel } from '../../../model/workshop/workshopSiteVisitModel';
import { AppUtils } from '../../apputils/app.utils';

@Component({
  selector: 'app-view-workshop-site-visit',
  templateUrl: './view-workshop-site-visit.component.html',
  styleUrls: ['./view-workshop-site-visit.component.scss'],
  standalone: false,
})
export class ViewWorkshopSiteVisitComponent {
  isSiteVisit = false;
  workshopRes: any;
  workshop: WorkshopSiteVisitModel;
  siteVisit: WorkshopSiteVisitModel;
  siteVisitRes: any;
  canReschedule = false;
  siteVisitType = 'OPTIONAL';
  /** Coordinator View Details — show schedule info only (no accept/reschedule actions). */
  viewOnly = false;

  constructor(
    public dialogRef: MatDialogRef<ViewWorkshopSiteVisitComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: MatSnackBar,
  ) {
    this.viewOnly = !!data?.viewOnly;
    const which = String(data?.which ?? '');
    const isSiteVisitContext =
      which === 'siteVisit' || which === 'mandatory_siteVisit';
    this.siteVisitType =
      data?.site_visit_type ||
      (which === 'mandatory_siteVisit' ? 'MANDATORY' : 'OPTIONAL');
    this.isSiteVisit = isSiteVisitContext;

    // Only show the section for the page the user opened from
    if (which === 'workshop') {
      this.workshop = data?.wokshop;
      if (this.workshop) {
        this.workshopRes = AppUtils.refreshUi(this.workshop);
      }
    } else if (isSiteVisitContext) {
      if (this.siteVisitType === 'MANDATORY' && data?.mandatorySiteVisit) {
        this.siteVisit = data.mandatorySiteVisit;
      } else if (data?.siteVisitSch) {
        this.siteVisit = data.siteVisitSch;
      } else if (data?.mandatorySiteVisit) {
        this.siteVisit = data.mandatorySiteVisit;
      } else if (data?.wokshop) {
        // Coordinator View Details passes the schedule model as wokshop
        this.siteVisit = data.wokshop;
      }
      if (this.siteVisit) {
        this.siteVisitRes = AppUtils.refreshUi(this.siteVisit);
        if (this.siteVisit.site_visit_type) {
          this.siteVisitType = String(this.siteVisit.site_visit_type).toUpperCase();
        }
      }
    } else {
      // Legacy: show whatever was provided
      this.workshop = data?.wokshop;
      if (this.workshop) {
        this.workshopRes = AppUtils.refreshUi(this.workshop);
      }
      if (data?.mandatorySiteVisit && this.siteVisitType === 'MANDATORY') {
        this.siteVisit = data.mandatorySiteVisit;
        this.siteVisitRes = AppUtils.refreshUi(this.siteVisit);
      } else if (data?.siteVisitSch) {
        this.siteVisit = data.siteVisitSch;
        this.siteVisitRes = AppUtils.refreshUi(this.siteVisit);
      }
    }
  }

  /** Top-left heading — never use API `name` (often "Site visits"). */
  get headingName(): string {
    if (this.workshop && !this.siteVisit) {
      return 'Workshop';
    }
    if (this.siteVisit && !this.workshop) {
      return this.siteVisitType === 'MANDATORY'
        ? 'Final Site Visit'
        : 'Optional Site Visit';
    }
    if (this.workshop && this.siteVisit) {
      return 'Workshop & Site Visit';
    }
    return 'Workshop';
  }

  get subtitle(): string {
    return 'Schedule details';
  }

  /** Top-right title with proper spacing. */
  getTitle(): string {
    const hasWorkshop = !!this.workshop;
    const hasSiteVisit = !!this.siteVisit;
    const mandatory = this.siteVisitType === 'MANDATORY';

    if (hasWorkshop && hasSiteVisit) {
      return mandatory
        ? 'Optional Workshop / Mandatory Site Visit'
        : 'Optional Workshop / Site Visit';
    }
    if (hasWorkshop) {
      return 'Optional Workshop';
    }
    if (hasSiteVisit) {
      return mandatory ? 'Mandatory Site Visit' : 'Optional Site Visit';
    }
    return 'Optional Workshop / Site Visit';
  }

  statusLabel(res: any): string {
    return res?.status?.trim() ? res.status : 'To be scheduled';
  }

  isConfirmedStatus(res: any): boolean {
    if (res?.isConfirmed) {
      return true;
    }
    const label = String(res?.status ?? '')
      .trim()
      .toLowerCase();
    return label === 'confirmed' || label === 'done';
  }

  onCancelRequest = (event: any) => {
    this.dialogRef.close({ eve: event });
  };

  onClickScheduleRequest = (fromWhich: string) => {
    const updateResponse = {
      eve: '',
      result: '',
      id: '',
      isSiteVisitAndWorkshopScheduled: false,
      from: '',
    };
    if (fromWhich === 'workshop') {
      updateResponse.eve = this.workshopRes.txtActionButton;
      updateResponse.id = this.workshop._id;
      updateResponse.result = this.workshopRes.txtActionButton;
      updateResponse.from = 'workshop';
    } else {
      updateResponse.eve = this.siteVisitRes.txtActionButton;
      updateResponse.id = this.siteVisit._id;
      updateResponse.result = this.siteVisitRes.txtActionButton;
      updateResponse.from = 'siteVisit';
    }
    this.dialogRef.close(updateResponse);
  };

  showActions = (fromWhich: string) => {
    if (this.viewOnly) {
      return false;
    }
    if (fromWhich === 'siteVisit') {
      return !!this.siteVisitRes?.txtActionButton?.length;
    }
    return !!this.workshopRes?.txtActionButton?.length;
  };

  onClickProposedStatusRequest = (type: string) => {
    const visitType = this.siteVisitType === 'MANDATORY' ? 'MANDATORY' : type;
    this.dialogRef.close({ result: 'ReSchedule', from: visitType });
  };

  onClose = () => {
    this.dialogRef.close();
  };
}
